import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { StoreReservationSettings } from "@/models/storeModel";
import { toast } from "@/hooks/use-toast";
import { useStoreReservationStore } from "@/store/storeReservationStore";
import { generateStoreSlug } from "@/services/reservations/reservationService";
import { useTranslation } from "react-i18next";
import { useStoreManagementStore } from "@/store/storeManagement";

const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

interface ReservationSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  currentSettings: StoreReservationSettings | null;
  onSettingsUpdate: (settings: StoreReservationSettings) => void;
}

export function ReservationSettings({
  open,
  onOpenChange,
  storeId,
  currentSettings,
  onSettingsUpdate,
}: ReservationSettingsProps) {
  const { t } = useTranslation();
  const { updateSettings } = useStoreReservationStore();
  const { selectedStore } = useStoreManagementStore();
  const [loading, setLoading] = useState(false);
  const [publicSlug, setPublicSlug] = useState("");
  const [isPublicBookingEnabled, setIsPublicBookingEnabled] = useState(true);
  const [maxPartySize, setMaxPartySize] = useState(10);
  const [bookingWindowDays, setBookingWindowDays] = useState(30);
  const [operatingHours, setOperatingHours] = useState<
    Record<string, { open: string; close: string; closed: boolean }>
  >(() => {
    // Initialize with default values for all days
    const defaultHours = DAYS_OF_WEEK.reduce(
      (acc, day) => ({
        ...acc,
        [day]: { open: "09:00", close: "17:00", closed: false },
      }),
      {}
    );

    // If we have current settings, use those
    if (currentSettings?.operating_hours) {
      return currentSettings.operating_hours;
    }

    // If we have store business hours, use those
    if (selectedStore?.business_hours) {
      return selectedStore.business_hours;
    }

    return defaultHours;
  });

  useEffect(() => {
    if (currentSettings) {
      setPublicSlug(currentSettings.public_slug);
      setIsPublicBookingEnabled(currentSettings.is_public_booking_enabled);
      setMaxPartySize(currentSettings.max_party_size);
      setBookingWindowDays(currentSettings.booking_window_days);

      // Update operating hours if we have new data
      if (selectedStore?.business_hours) {
        const hours = selectedStore.business_hours;
        // Ensure we have a valid object
        if (typeof hours === "object" && hours !== null) {
          setOperatingHours(hours);
        }
      } else if (currentSettings.operating_hours) {
        const hours = currentSettings.operating_hours;
        // Ensure we have a valid object
        if (typeof hours === "object" && hours !== null) {
          setOperatingHours(hours);
        }
      }
    } else {
      setPublicSlug(generateStoreSlug(storeId));
    }
  }, [currentSettings, storeId, selectedStore]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const settings = await updateSettings({
        store_id: storeId,
        public_slug: publicSlug,
        is_public_booking_enabled: isPublicBookingEnabled,
        max_party_size: maxPartySize,
        booking_window_days: bookingWindowDays,
        operating_hours: operatingHours,
      });

      if (settings) {
        onSettingsUpdate(settings);
        toast({
          title: t("reservationSettings.toast.success.title"),
          description: t("reservationSettings.toast.success.description"),
        });
        onOpenChange(false);
      } else {
        throw new Error("Failed to update settings");
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      toast({
        title: t("reservationSettings.toast.error.title"),
        description: t("reservationSettings.toast.error.description"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateDayHours = (
    day: string,
    field: "open" | "close" | "closed",
    value: string | boolean
  ) => {
    setOperatingHours((prev) => {
      const newHours = {
        ...prev,
        [day]: {
          ...prev[day],
          [field]: value,
        },
      };
      return newHours;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("reservationSettings.title")}</DialogTitle>
          <DialogDescription>
            {t("reservationSettings.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Public Booking Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t("reservationSettings.publicBooking.title")}
              </CardTitle>
              <CardDescription>
                {t("reservationSettings.publicBooking.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="public-booking">
                  {t("reservationSettings.publicBooking.enable")}
                </Label>
                <Switch
                  id="public-booking"
                  checked={isPublicBookingEnabled}
                  onCheckedChange={setIsPublicBookingEnabled}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="public-slug">
                  {t("reservationSettings.publicBooking.url")}
                </Label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 text-sm text-gray-500 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">
                    {window.location.origin}/book/
                  </span>
                  <Input
                    id="public-slug"
                    value={publicSlug}
                    onChange={(e) => setPublicSlug(e.target.value)}
                    className="rounded-l-none"
                    placeholder="store-name"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Booking Limits */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t("reservationSettings.bookingLimits.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="max-party">
                  {t("reservationSettings.bookingLimits.maxParty")}
                </Label>
                <Input
                  id="max-party"
                  type="number"
                  value={maxPartySize}
                  onChange={(e) =>
                    setMaxPartySize(parseInt(e.target.value) || 10)
                  }
                  min="1"
                  max="50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="booking-window">
                  {t("reservationSettings.bookingLimits.bookingWindow")}
                </Label>
                <Input
                  id="booking-window"
                  type="number"
                  value={bookingWindowDays}
                  onChange={(e) =>
                    setBookingWindowDays(parseInt(e.target.value) || 30)
                  }
                  min="1"
                  max="365"
                />
                <p className="text-sm text-gray-500">
                  {t("reservationSettings.bookingLimits.bookingWindowDesc")}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("reservationSettings.actions.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading
              ? t("reservationSettings.actions.saving")
              : t("reservationSettings.actions.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
